/**
 * FauxDS RadioGroup - Recreated from Figma MCP
 *
 * ⚠️ This is a SIMPLIFIED RECREATION of the Slate RadioGroup component for prototyping.
 * It is NOT the real @faire/slate RadioGroup and should NOT be used for:
 * - Engineering handoff
 * - Design specs
 * - Production code
 *
 * For the real Slate RadioGroup, see: @faire/slate/components/radio
 *
 * This component was recreated from Figma MCP design specs:
 * - Figma node: 47696:10469 (Slate Radio Group)
 * - Design tokens extracted from Figma variables
 * - All variants verified: Amount (2-12), Disabled (true/false), Error (true/false)
 * - Header label support verified
 * - Error text support verified
 * - Proper spacing maintained for multiple radios in a line
 */
import { cn } from "@faire/prototype-playground/lib/utils";
import React, { forwardRef } from "react";

import Radio from "./Radio";

export interface RadioGroupOption {
  /**
   * The value of the radio option
   */
  value: string;
  /**
   * The label text for the radio option
   */
  label: string;
}

export interface RadioGroupProps {
  /**
   * Header label text
   * @default "Header label"
   */
  label?: string;
  /**
   * Whether the entire group is disabled
   * @default false
   */
  disabled?: boolean;
  /**
   * Error message text
   * @default "Error text"
   */
  error?: boolean | string;
  /**
   * Custom options array (overrides default options based on amount)
   */
  options: RadioGroupOption[];
  /**
   * Selected value (controlled)
   */
  checked?: string;
  /**
   * Change handler
   */
  onChecked?: (value: string) => void;
  /**
   * Radio group name attribute
   */
  name?: string;
  /**
   * **playground only**
   * Optional className for additional styling
   */
  className?: string;
}

/**
 * RadioGroup - FauxDS (recreated from Figma MCP)
 *
 * RadioGroup allows users to select only one option among a set of options.
 * Options are typically supported by text labels.
 *
 * Usage tips:
 * - Provide a clear label for each option
 * - Pre-select a default option when possible
 * - Each option must be mutually exclusive
 * - If possible, order options in a way that makes logical sense
 *
 * This implementation was created by extracting design specs directly from Figma.
 * Spacing is maintained when multiple radios are used in a line.
 */
const RadioGroup = forwardRef<HTMLDivElement, RadioGroupProps>(
  (
    {
      className = "",
      label,
      disabled = false,
      error,
      options,
      checked,
      onChecked: onChange,
      name,
    },
    ref
  ) => {
    const groupId = React.useId();
    const groupName = name || `radio-group-${groupId}`;

    const handleChange = (optionValue: string) => {
      if (!disabled) {
        onChange?.(optionValue);
      }
    };

    // Header label color based on state
    const headerLabelColor = disabled
      ? "text-[var(--slate-color-action-text-disabled)]" // #757575
      : error
        ? "text-[var(--slate-color-action-text-critical)]" // #921100
        : "text-[var(--slate-color-action-text-default)]"; // #333333

    // Error text color
    const errorTextColor = "text-[var(--slate-color-action-text-critical)]"; // #921100

    return (
      <div
        ref={ref}
        className={cn("flex flex-col", className)}
        role="radiogroup"
      >
        {/* Header Label */}
        {label && (
          <label
            className={cn("mb-[var(--slate-dimensions-2)]", headerLabelColor)}
            style={{
              fontFamily: "var(--slate-typography-family-sans-serif)",
              fontSize: "var(--slate-typography-font-size-200)",
              fontWeight: "var(--slate-typography-weight-regular)",
              lineHeight: "var(--slate-typography-line-height-200)",
              letterSpacing: "var(--slate-typography-letter-spacing-200)",
            }}
          >
            {label}
          </label>
        )}

        {/* Radio Options */}
        <div
          className="flex flex-col gap-[var(--slate-dimensions-2)]"
          style={{
            gap: "var(--slate-dimensions-2)", // 8px spacing between radios
          }}
        >
          {options.map((option, index) => {
            const isChecked =
              checked !== undefined ? checked === option.value : false;

            return (
              <Radio
                key={option.value}
                label={option.label}
                checked={isChecked}
                disabled={disabled}
                name={groupName}
                value={option.value}
                onChange={(e) => handleChange(e.target.value)}
              />
            );
          })}
        </div>

        {/* Error Text */}
        {typeof error === "string" && (
          <p
            className={cn("mt-[var(--slate-dimensions-2)]", errorTextColor)}
            style={{
              fontFamily: "var(--slate-typography-family-sans-serif)",
              fontSize: "var(--slate-typography-font-size-100)",
              fontWeight: "var(--slate-typography-weight-regular)",
              lineHeight: "var(--slate-typography-line-height-100)",
              letterSpacing: "var(--slate-typography-letter-spacing-200)",
            }}
          >
            {error}
          </p>
        )}
      </div>
    );
  }
);

RadioGroup.displayName = "RadioGroup";

export default RadioGroup;
